import { BaseDialog } from '@/components/BaseDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useConfirm } from '@/hooks/useConfirm'
import { isApiError } from '@/utils/error'
import { getMutationOptions } from '@/utils/mutation'
import { showError } from '@/utils/toast'
import { Check, Edit2, Plus, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useCreateProductCategory,
  useDeleteProductCategory,
  useGetProductCategories,
  useUpdateProductCategory,
} from '../hooks/useProduct'
import { ProductCategory } from '../types/product'

type ProductCategoryForm = {
  id: string
  name: string
}

export function ProductCategoryDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const { data: productCategories = [], isLoading: isLoadingProductCategories } = useGetProductCategories(
    undefined,
    open,
  )

  const createProductCategory = useCreateProductCategory()
  const updateProductCategory = useUpdateProductCategory()
  const deleteProductCategory = useDeleteProductCategory()

  const [isCreating, setIsCreating] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')

  const isLoading = isLoadingProductCategories || createProductCategory.isPending || updateProductCategory.isPending

  function handleCreate() {
    setIsCreating(true)
    setEditingName('')
  }

  function handleCreateCancel() {
    setIsCreating(false)
    setEditingName('')
  }

  function handleEdit(category: ProductCategoryForm) {
    setEditingId(category.id)
    setEditingName(category.name)
  }

  function handleEditCancel() {
    setEditingId(null)
    setEditingName('')
  }

  const mutationOptions = {
    onError: (error: unknown) => {
      if (isApiError(error)) {
        showError(error.message)
      }
    },
  }

  function handleCreateSubmit() {
    if (!editingName.trim()) return
    const options = getMutationOptions({
      successMessage: t('equipment.category.toast.addSuccess'),
      onSuccess: () => {
        handleCreateCancel()
      },
    })
    createProductCategory.mutate({ payload: { name: editingName } }, options)
  }

  function handleEditSubmit(id: string) {
    if (!editingName.trim()) return
    const options = getMutationOptions({
      successMessage: t('equipment.category.toast.editSuccess'),
      onSuccess: () => {
        handleEditCancel()
      },
    })
    updateProductCategory.mutate({ id, payload: { name: editingName } }, options)
  }

  const handleDeleteCategory = async (productCategory: ProductCategory) => {
    const confirmed = await confirm({
      variant: 'destructive',
      title: t('common.deleteConfirmTitle'),
      description: t('equipment.category.confirmDeleteDescription', { category: productCategory.name }),
    })

    if (confirmed) {
      const options = getMutationOptions({
        successMessage: t('equipment.category.toast.deleteSuccess'),
        onSuccess: () => {
          handleEditCancel()
        },
      })
      deleteProductCategory.mutate(productCategory.id, options)
    }
  }

  return (
    <BaseDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('equipment.category.title')}
      isLoading={isLoading}
      contentTestId="product-category-dialog"
    >
      <div data-testid="product-category-list" className="flex flex-col gap-4">
        {productCategories.map(c => {
          const isEditing = editingId === c.id
          const isAnotherEditing = editingId !== null && editingId !== c.id

          return (
            <div
              key={c.id}
              data-testid={`product-category-item-${c.id}`}
              className="flex flex-col sm:flex-row items-center justify-between py-1 px-2 gap-2"
            >
              {isEditing ? (
                <Input
                  data-testid="product-category-name-input"
                  value={editingName}
                  onChange={e => setEditingName(e.target.value)}
                  className="h-9"
                />
              ) : (
                <Label className="text-sm">{c.name}</Label>
              )}

              <div className="flex gap-2">
                {isEditing ? (
                  <>
                    <Button
                      data-testid="product-category-submit"
                      className="border-success/30 text-success hover:text-success hover:bg-success/10 size-8"
                      variant="outline"
                      size="icon"
                      onClick={() => handleEditSubmit(c.id)}
                      disabled={!editingName.trim()}
                    >
                      <Check />
                    </Button>

                    <Button
                      data-testid="product-category-cancel"
                      className="size-8"
                      size="icon"
                      variant="outline"
                      onClick={handleEditCancel}
                    >
                      <X />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      data-testid={`product-category-edit-${c.id}`}
                      className="size-8"
                      variant="outline"
                      size="icon"
                      onClick={() => handleEdit(c)}
                      disabled={isAnotherEditing || isCreating}
                    >
                      <Edit2 />
                    </Button>

                    <Button
                      data-testid={`product-category-delete-${c.id}`}
                      className="size-8 border-destructive/30 text-destructive hover:text-destructive hover:bg-destructive/10"
                      variant="outline"
                      size="icon"
                      onClick={() => handleDeleteCategory(c)}
                      disabled={isAnotherEditing || isCreating}
                    >
                      <Trash2 />
                    </Button>
                  </>
                )}
              </div>
            </div>
          )
        })}
        {isCreating && (
          <div className="flex flex-col sm:flex-row items-center justify-between p-2 gap-2">
            <Input
              data-testid="product-category-create-name-input"
              value={editingName}
              onChange={e => setEditingName(e.target.value)}
              placeholder={t('equipment.category.placeholder.name')}
            />

            <div className="col-span-1 flex gap-2 justify-end">
              <Button
                data-testid="product-category-create-submit"
                className="border-success/30 text-success hover:text-success hover:bg-success/10 size-8"
                variant="outline"
                size="icon"
                onClick={handleCreateSubmit}
                disabled={!editingName.trim()}
              >
                <Check />
              </Button>

              <Button
                data-testid="product-category-create-cancel"
                className="size-8"
                size="icon"
                variant="outline"
                onClick={handleCreateCancel}
              >
                <X />
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="col-span-3 flex justify-end px-2 mt-4">
        <Button
          data-testid="product-category-create-button"
          className="size-8"
          variant="outline"
          size="icon"
          onClick={handleCreate}
          disabled={isLoading || editingId !== null || isCreating}
        >
          <Plus />
        </Button>
      </div>
    </BaseDialog>
  )
}
